import React from 'react';
import './targetVsAchievementTable.scss';
import NoDataFound from '../../component/noDataComponent/NoDataFound';
import { formatMonth, isCurrentMonth } from '../../constants/dateTimeUtils/dateTimeUtils';


const renderSkeletonRows = (count = 5) =>
  Array.from({ length: count }).map((_, i) => (
    <tr key={`sk-${i}`} className="skeleton-row">
      <td><span className="skeleton text w-24" /></td>
      <td><span className="skeleton text w-24" /></td>
      <td><span className="skeleton text w-20" /></td>
      <td><span className="skeleton text w-20" /></td>
      <td><span className="skeleton text w-20" /></td>
      <td><span className="skeleton text w-20" /></td>
      <td><span className="skeleton text w-20" /></td>
      <td><span className="skeleton text w-20" /></td>
      <td><span className="skeleton text w-20" /></td>
    </tr>
  ));

const TargetVsAchievementTable = ({
  data,
  totalPages,
  currentPage,
  setCurrentPage,
  loading = false,
  totalCount = 0,
  pageSize = 10,
  pageWindow = [],
  selectedAPS,
  onViewDetail
}) => {
  // Calculate result info
  const startIndex = totalCount > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const endIndex = Math.min(currentPage * pageSize, totalCount);
  const formatBackendValue = (value, options) => {
    if (value === null || value === undefined) return "-----";

    // If value is already string → show directly
    if (typeof value === "string") return value;

    // If value is number → format
    return Number(value).toLocaleString("en-IN", options);
  };


  return (
    <div className="target-achievement-table-wrapper">
      <div className="table-container">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Month</th>
                <th>Monthly Plan</th>
                <th>Monthly Actual</th>
                <th>Monthly Achievement %</th>
                <th>YTD Plan</th>
                <th>YTD Actual</th>
                <th>YTD Achievement %</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                renderSkeletonRows(5)
              ) : data.length === 0 ? (
                <tr>
                  <td colSpan="8">
                    <NoDataFound />
                  </td>
                </tr>
              ) : (
                data.map((row, index) => (
                  <tr
                    key={row.id || index}
                    className={isCurrentMonth(row.month) ? "current-month-row" : ""}
                  >
                    <td className="month-cell">{formatMonth(row.month)}</td>

                    <td className="plan-cell">
                      {formatBackendValue(row.monthly_plan, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </td>

                    <td className="actual-cell">
                      {formatBackendValue(row.monthly_actual)}
                    </td>

                    <td className="achievement-cell">
                      {typeof row.monthly_pct === "number"
                        ? `${Number(row.monthly_pct).toFixed(2)}%`
                        : row.monthly_pct ?? "-----"}
                    </td>

                    <td className="ytd-plan-cell">
                      {formatBackendValue(row.ytd_plan, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </td>

                    <td className="ytd-actual-cell">
                      {formatBackendValue(row.ytd_actual)}
                    </td>

                    <td className="ytd-achievement-cell">
                      {typeof row.ytd_pct === "number"
                        ? `${Number(row.ytd_pct).toFixed(2)}%`
                        : row.ytd_pct ?? "-----"}
                    </td>

                    <td className="action-cell">
                      <button
                        className="view-detail-link"
                        onClick={() => onViewDetail && onViewDetail(row.month)}
                      >
                        View Detail
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="table-footer">
          {loading ? (
            <div className="footer-skeleton">
              <span className="skeleton text w-40" />
              <div className="pagination">
                <span className="skeleton pill w-16" />
                <span className="skeleton pill w-8" />
                <span className="skeleton pill w-8" />
                <span className="skeleton pill w-8" />
                <span className="skeleton pill w-16" />
              </div>
            </div>
          ) : (
            <>
              <div className="results-info">
                {totalCount > 0 ? `Showing ${startIndex} to ${endIndex} of ${totalCount} results` : 'No results'}
              </div>
              <div className="pagination">
                <button
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                  className="prev-btn"
                >
                  Previous
                </button>
                {pageWindow.map((p, idx) =>
                  typeof p === "string" ? (
                    <button key={p + idx} className="page-btn ellipsis-btn" disabled>
                      …
                    </button>
                  ) : (
                    <button
                      key={p}
                      onClick={() => setCurrentPage(p)}
                      className={`page-btn ${currentPage === p ? 'active' : ''}`}
                    >
                      {p}
                    </button>
                  )
                )}
                <button
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(currentPage + 1)}
                  className="next-btn"
                >
                  Next
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TargetVsAchievementTable;
